import { useEffect, useMemo, useState } from 'react';
import { Bell } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import SearchInput from '../components/SearchInput';
import { notificationsAPI } from '../services/api';
import { filterBySearch } from '../utils/search';

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = () => notificationsAPI.list().then(({ data }) => setItems(data));

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, []);

  const markRead = async (id) => {
    await notificationsAPI.markRead(id);
    load();
  };

  const filteredItems = useMemo(
    () => filterBySearch(items, search, [
      'title',
      'message',
      'event_type',
      'employee_name',
      'channel',
    ]),
    [items, search]
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Notifications &amp; Alerts</h1>
        <p className="text-slate-500 mt-1">M10 — enrollment, claims, renewal and premium alerts (in-app)</p>
      </div>

      <SearchInput
        className="max-w-md"
        placeholder="Search notifications…"
        value={search}
        onChange={setSearch}
      />

      <div className="space-y-3">
        {items.length === 0 && (
          <div className="card text-center text-slate-500 py-8">No notifications yet.</div>
        )}
        {items.length > 0 && filteredItems.length === 0 && (
          <p className="text-center py-8 text-slate-500">No notifications match your search.</p>
        )}
        {filteredItems.map((n) => (
          <div key={n.id} className={`card flex gap-4 ${n.is_read ? 'opacity-75' : 'border-l-4 border-l-primary-500'}`}>
            <div className="bg-primary-50 p-2 rounded-lg h-fit">
              <Bell className="w-5 h-5 text-primary-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between gap-2">
                <h3 className="font-semibold">{n.title}</h3>
                <span className="text-xs text-slate-400 whitespace-nowrap">
                  {new Date(n.sent_at).toLocaleString('en-IN')}
                </span>
              </div>
              <p className="text-sm text-slate-600 mt-1">{n.message}</p>
              <div className="flex flex-wrap gap-2 mt-2 text-xs text-slate-500">
                <span className="capitalize">{n.event_type?.replace(/_/g, ' ')}</span>
                {n.employee_name && <span>· {n.employee_name}</span>}
                <span>· {n.channel}</span>
              </div>
              {!n.is_read && (
                <button type="button" onClick={() => markRead(n.id)} className="text-sm text-primary-600 mt-2">
                  Mark as read
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
